import { useEffect, useRef, useCallback } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Minimum distance for a swipe (px)
  velocityThreshold?: number; // Minimum velocity for a swipe (px/ms)
  preventDefault?: boolean;
  touchOnly?: boolean; // Only respond to touch events, not mouse
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

export const useSwipeGestures = (options: SwipeGestureOptions = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    velocityThreshold = 0.3,
    preventDefault = true,
    touchOnly = true
  } = options;

  const elementRef = useRef<HTMLElement>(null);
  const startPoint = useRef<TouchPoint | null>(null);
  const isTracking = useRef(false);

  const getEventCoordinates = useCallback((event: TouchEvent | MouseEvent) => {
    if ('touches' in event) {
      return {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }
    return {
      x: event.clientX,
      y: event.clientY
    };
  }, []);

  const handleStart = useCallback((event: TouchEvent | MouseEvent) => {
    if (touchOnly && !('touches' in event)) return;
    
    const coords = getEventCoordinates(event);
    startPoint.current = {
      x: coords.x,
      y: coords.y,
      time: Date.now()
    };
    isTracking.current = true;

    if (preventDefault) {
      event.preventDefault();
    }
  }, [getEventCoordinates, preventDefault, touchOnly]);

  const handleEnd = useCallback((event: TouchEvent | MouseEvent) => {
    if (!isTracking.current || !startPoint.current) return;
    
    let coords;
    if ('changedTouches' in event) {
      coords = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY
      };
    } else {
      coords = {
        x: event.clientX,
        y: event.clientY
      };
    }

    const endTime = Date.now();
    const deltaX = coords.x - startPoint.current.x;
    const deltaY = coords.y - startPoint.current.y;
    const deltaTime = endTime - startPoint.current.time;
    
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    
    // Calculate velocity
    const velocityX = absX / deltaTime;
    const velocityY = absY / deltaTime;
    
    // Determine if this qualifies as a swipe
    const isHorizontalSwipe = absX > threshold && absX > absY && velocityX > velocityThreshold;
    const isVerticalSwipe = absY > threshold && absY > absX && velocityY > velocityThreshold;
    
    if (isHorizontalSwipe) {
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    } else if (isVerticalSwipe) {
      if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      } else if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      }
    }

    // Reset tracking
    startPoint.current = null;
    isTracking.current = false;

    if (preventDefault) {
      event.preventDefault();
    }
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold, velocityThreshold, preventDefault]);

  const handleMove = useCallback((event: TouchEvent | MouseEvent) => {
    if (!isTracking.current) return;
    
    if (preventDefault) {
      event.preventDefault();
    }
  }, [preventDefault]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Add touch event listeners
    element.addEventListener('touchstart', handleStart, { passive: !preventDefault });
    element.addEventListener('touchend', handleEnd, { passive: !preventDefault });
    element.addEventListener('touchmove', handleMove, { passive: !preventDefault });

    // Add mouse event listeners if not touch-only
    if (!touchOnly) {
      element.addEventListener('mousedown', handleStart);
      element.addEventListener('mouseup', handleEnd);
      element.addEventListener('mousemove', handleMove);
    }

    return () => {
      element.removeEventListener('touchstart', handleStart);
      element.removeEventListener('touchend', handleEnd);
      element.removeEventListener('touchmove', handleMove);
      
      if (!touchOnly) {
        element.removeEventListener('mousedown', handleStart);
        element.removeEventListener('mouseup', handleEnd);
        element.removeEventListener('mousemove', handleMove);
      }
    };
  }, [handleStart, handleEnd, handleMove, touchOnly, preventDefault]);

  return elementRef;
};

// Utility hook for common navigation swipes
export const useSwipeNavigation = (onPrevious: () => void, onNext: () => void) => {
  return useSwipeGestures({
    onSwipeLeft: onNext,
    onSwipeRight: onPrevious,
    threshold: 75, // Slightly higher threshold for navigation
    velocityThreshold: 0.4, // Require more intentional swipes
    preventDefault: false // Allow other touch interactions
  });
};